import { useEffect, useState } from "react";
import { Copy, ArrowsClockwise, GoogleLogo, AppleLogo, Calendar as CalendarIcon, GlobeHemisphereEast } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiErrorDetail, API } from "../lib/api";

export default function CalendarSync() {
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rotating, setRotating] = useState(false);
  const [tz, setTz] = useState("UTC");
  const [savingTz, setSavingTz] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [tokRes, tzRes] = await Promise.all([
          api.get("/calendar/feed-token"),
          api.get("/profile/timezone"),
        ]);
        if (!alive) return;
        setToken(tokRes.data.token);
        const stored = tzRes.data.timezone;
        const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
        // Auto-set on first login: if stored is the UTC default and browser knows better, persist it once.
        if (stored === "UTC" && browserTz && browserTz !== "UTC") {
          try {
            const upd = await api.put("/profile/timezone", { timezone: browserTz });
            setTz(upd.data.timezone);
          } catch (_e) {
            setTz(stored);
          }
        } else {
          setTz(stored);
        }
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const useBrowserTz = async () => {
    const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!browserTz) {
      toast.error("Couldn't detect your browser timezone");
      return;
    }
    if (browserTz === tz) {
      toast.info("Already using this timezone");
      return;
    }
    setSavingTz(true);
    try {
      const { data } = await api.put("/profile/timezone", { timezone: browserTz });
      setTz(data.timezone);
      toast.success(`Timezone set to ${data.timezone}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingTz(false);
    }
  };

  const feedUrl = token ? `${API}/calendar/feed/${token}.ics` : "";
  const googleAddUrl = token ? `https://calendar.google.com/calendar/r?cid=${encodeURIComponent(feedUrl.replace(/^https?:\/\//, "webcal://"))}` : "";

  const copy = async (text, label = "URL") => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch (_e) {
      toast.error("Couldn't copy — please copy manually");
    }
  };

  const rotate = async () => {
    if (!window.confirm("Rotate calendar URL? Your previously-shared calendar subscriptions will stop syncing and need to be re-added.")) return;
    setRotating(true);
    try {
      const { data } = await api.post("/calendar/feed-token/rotate");
      setToken(data.token);
      toast.success("Calendar URL rotated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setRotating(false);
    }
  };

  if (loading) {
    return <div className="card-flat p-6 text-zinc-500 tracker" data-testid="calendar-sync-loading">Loading calendar sync…</div>;
  }

  return (
    <div className="card-flat p-6 space-y-5" data-testid="calendar-sync">
      <div className="flex items-start gap-3">
        <CalendarIcon size={22} weight="duotone" className="text-brand mt-1" />
        <div className="flex-1">
          <div className="font-display text-2xl leading-tight">Sync to Google / Apple Calendar</div>
          <p className="text-zinc-400 text-sm mt-1">
            Subscribe to your sessions feed in any calendar app. Updates auto-sync every couple of hours — no app installs required.
          </p>
        </div>
      </div>

      <div>
        <label className="tracker block mb-2">Your private calendar URL</label>
        <div className="flex items-center gap-2">
          <input
            readOnly
            value={feedUrl}
            className="flex-1 px-3 py-2 border font-mono text-xs"
            data-testid="calendar-feed-url"
            onFocus={(e) => e.target.select()}
          />
          <button
            type="button"
            onClick={() => copy(feedUrl, "Calendar URL")}
            className="btn-ghost !py-2 !px-3"
            data-testid="calendar-copy-url"
            title="Copy URL"
          >
            <Copy size={14} weight="bold" />
          </button>
        </div>
        <div className="tracker text-zinc-500 mt-2 text-[10px]">
          Keep this URL private — anyone with it can view your session schedule.
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <a
          href={googleAddUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-ghost justify-center !py-3"
          data-testid="add-google-calendar"
        >
          <GoogleLogo size={16} weight="bold" /> Add to Google Calendar
        </a>
        <a
          href={feedUrl.replace(/^https?:\/\//, "webcal://")}
          className="btn-ghost justify-center !py-3"
          data-testid="add-apple-calendar"
        >
          <AppleLogo size={16} weight="bold" /> Add to Apple Calendar
        </a>
      </div>

      <details className="text-sm" data-testid="calendar-instructions">
        <summary className="cursor-pointer tracker text-zinc-400 hover:text-white">Manual setup instructions</summary>
        <div className="mt-3 space-y-3 text-zinc-400 text-xs leading-relaxed">
          <div>
            <div className="font-semibold text-white mb-1">Google Calendar</div>
            <ol className="list-decimal list-inside space-y-1 pl-1">
              <li>Open Google Calendar on desktop</li>
              <li>Left sidebar → "Other calendars" → "+" → "From URL"</li>
              <li>Paste your URL above and click "Add calendar"</li>
            </ol>
          </div>
          <div>
            <div className="font-semibold text-white mb-1">Apple Calendar (Mac/iOS)</div>
            <ol className="list-decimal list-inside space-y-1 pl-1">
              <li>Mac: Calendar → File → "New Calendar Subscription…"</li>
              <li>iOS: Settings → Calendar → Accounts → "Add Account" → "Other" → "Add Subscribed Calendar"</li>
              <li>Paste your URL and choose how often to refresh (every 15 min recommended)</li>
            </ol>
          </div>
          <div>
            <div className="font-semibold text-white mb-1">Outlook</div>
            <p>Calendar → "Add Calendar" → "Subscribe from web" → paste URL.</p>
          </div>
        </div>
      </details>

      <div className="pt-3 border-t border-white/10 space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap" data-testid="calendar-tz-row">
          <div className="flex items-center gap-2">
            <GlobeHemisphereEast size={14} weight="duotone" className="text-brand" />
            <div className="tracker text-zinc-500 text-[10px]">SESSION TIMEZONE</div>
            <span className="font-mono text-xs" data-testid="calendar-tz-value">{tz}</span>
          </div>
          <button
            type="button"
            onClick={useBrowserTz}
            disabled={savingTz}
            className="btn-ghost !py-2 !px-3 !text-[10px]"
            data-testid="calendar-tz-use-browser"
          >
            {savingTz ? "Saving…" : "Use my current timezone"}
          </button>
        </div>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="tracker text-zinc-500 text-[10px]">
            Lost or shared this URL by accident? Rotate it to revoke access.
          </div>
          <button
            type="button"
            onClick={rotate}
            disabled={rotating}
            className="btn-ghost !py-2 !px-3 !text-[10px]"
            data-testid="calendar-rotate"
          >
            <ArrowsClockwise size={12} weight="bold" /> {rotating ? "Rotating…" : "Rotate URL"}
          </button>
        </div>
      </div>
    </div>
  );
}
